/**
 * Re-Export-Schicht über `api.gen.ts` (von openapi-typescript erzeugt).
 *
 * Diese Datei enthält KEINE eigene Schema-Pflege mehr. Sie schränkt nur
 * `components['schemas']['…']`-Pfade auf kompakte, im Code häufig verwendete
 * Aliasnamen ein. Bei Schema-Änderungen:
 *
 *   1. `pnpm gen:types`
 *   2. tsc-Fehler in dieser Datei beheben (Drift sichtbar gemacht)
 *   3. Ggf. neue Aliase ergänzen
 *
 * exactOptionalPropertyTypes ist im tsconfig aktiv -> die generierten Types
 * verwenden bewusst `field?: T | null` (nicht `field: T | null | undefined`).
 */

import type { components } from './api.gen';

type Schemas = components['schemas'];

// --- Brand-Aliase für Lesbarkeit ----------------------------------------
export type UUID = string;
export type ISODateTime = string;
export type ISODate = string;

// --- Common --------------------------------------------------------------
export type ProblemDetails = Schemas['ProblemDetails'];
export type PaginationLinks = Schemas['PaginationLinks'];
export type PaginationMeta = Schemas['PaginationMeta'];
export type HealthStatus = Schemas['HealthStatus'];
export type VersionInfo = Schemas['VersionInfo'];

// --- Auth ----------------------------------------------------------------
export type LoginRequest = Schemas['LoginRequest'];
export type LoginResponse = Schemas['LoginResponse'];

// --- Audit ---------------------------------------------------------------
export type AuditEntry = Schemas['AuditEntry'];
export type AuditEntryList = Schemas['AuditEntryList'];

// --- Customer ------------------------------------------------------------
export type Customer = Schemas['Customer'];
export type CustomerCreate = Schemas['CustomerCreate'];
export type CustomerUpdate = Schemas['CustomerUpdate'];
export type CustomerList = Schemas['CustomerList'];
/** Backwards-compat für Hooks, die älteres Naming benutzen. */
export type CustomerListResponse = CustomerList;
export type MaintenanceWindow = NonNullable<Customer['maintenance_window']>;
export type CustomerContact = Schemas['CustomerContact'];
export type CustomerContactList = Schemas['CustomerContactList'];
export type CustomerContactCreate = Schemas['CustomerContactCreate'];
export type CustomerContactUpdate = Schemas['CustomerContactUpdate'];
export type CustomerContactSource = NonNullable<CustomerContact['source']>;
export type EffectiveRecipients = Schemas['EffectiveRecipients'];
export type EffectiveRecipient = Schemas['EffectiveRecipient'];
export type SuppressedRecipient = Schemas['SuppressedRecipient'];
export type SuppressedReason = NonNullable<SuppressedRecipient['reason']>;
export type RecipientOrigin = NonNullable<EffectiveRecipient['origin']>;

// --- Location ------------------------------------------------------------
export type Location = Schemas['Location'];
export type LocationCreate = Schemas['LocationCreate'];
export type LocationUpdate = Schemas['LocationUpdate'];
export type LocationList = Schemas['LocationList'];

// --- System --------------------------------------------------------------
// S8-06: keine lokale Erweiterung mehr nötig - service_type_codes,
// has_active_ssh_profile und die Inventar-Felder stehen im generierten Schema.
export type System = Schemas['System'];
export type SystemCreate = Schemas['SystemCreate'];
export type SystemUpdate = Schemas['SystemUpdate'];
export type SystemList = Schemas['SystemList'];
export type SystemListResponse = SystemList;
export type SystemRole = System['role'];
export type SystemRoleCount = Schemas['SystemRoleCount'];
export type SystemRolesResponse = Schemas['SystemRolesResponse'];
export type SystemFacetValue = Schemas['SystemFacetValue'];
export type SystemOsVersionFacet = Schemas['SystemOsVersionFacet'];
export type SystemFacetsResponse = Schemas['SystemFacetsResponse'];
// Vorschlagswert mit Kunden-Flag (vpn_refs, domains, proxmox_nodes).
export type SystemSuggestionValue = Schemas['SystemSuggestionValue'];
export type UpdatePolicy = NonNullable<System['update_policy']>;
export type SystemScope = NonNullable<System['scope']>;
// Belegte Anmeldewege (Migration 0040): 'passwort' | 'passwort+key' | 'key' |
// 'unbekannt' | 'manuell'. Der Yubikey-Schlüssel ersetzt das Tresor-Passwort
// nicht - er kommt als zweiter Weg dazu.
export type AuthMode = Schemas['AuthMode'];
export type Empfehlung = Schemas['Empfehlung'];

// --- Service & Connection ------------------------------------------------
export type ServiceType = Schemas['ServiceType'];
export type ServiceTypeFamily = ServiceType['family'];
export type Service = Schemas['Service'];
export type UnknownServiceString = Schemas['UnknownServiceString'];

/**
 * Handgepflegte Typen für die Connection-Profile-CRUD-API (Backend entsteht
 * parallel). Das alte `Schemas['ConnectionProfile']` im OpenAPI-Dokument
 * beschreibt den kind-basierten ods-Import-Entwurf und kennt weder
 * `protocol` noch `is_active` — der tatsächliche DB-Stand (Tabelle
 * `connection_profiles`) ist flach. Nach dem nächsten `pnpm gen:types`
 * mit aktualisiertem Contract auf `Schemas['…']`-Aliase umstellen
 * (Vorgehen wie bei den MaintenanceDue-Typen unten).
 */
export type ConnectionProtocol = 'ssh' | 'rdp' | 'https';

export interface ConnectionProfile {
  id: UUID;
  system_id: UUID;
  /** 'ssh' | 'rdp' | 'https'; weitere Werte aus Import-Altbestand tolerant als string. */
  protocol: string;
  username?: string | null;
  port?: number | null;
  notes?: string | null;
  is_active: boolean;
  created_at: ISODateTime;
}

export interface ConnectionProfileCreate {
  protocol: ConnectionProtocol;
  username?: string | null;
  port?: number | null;
  notes?: string | null;
}

export interface ConnectionProfileUpdate {
  protocol?: ConnectionProtocol;
  username?: string | null;
  port?: number | null;
  notes?: string | null;
  is_active?: boolean;
}

// --- Runs: Start/Confirm/Batch (Snapshot-Verzicht, Migration 0029) --------
// Die Hooks in useRuns.ts pflegen ihre Wire-Typen historisch von Hand; die
// Verzichts-Felder kommen aus den generierten Schemas, damit Frontend und
// Spec bei den Regeln (30 Zeichen, Ziffer im Beleg, ...) nicht driften.
export type RunCreate = Schemas['RunCreate'];
export type RunConfirm = Schemas['RunConfirm'];
export type RunCreateResponse = Schemas['RunCreateResponse'];
export type RunBatchRunResult = Schemas['RunBatchRunResult'];

// --- Import --------------------------------------------------------------
export type ImportReport = Schemas['ImportReport'];

// --- Sprint 2: Contracts / Sync ------------------------------------------
export type SyncStatus = Schemas['SyncStatus'];
export type Contract = Schemas['Contract'];
export type ContractList = Schemas['ContractList'];
export type ContractLineItem = Schemas['ContractLineItem'];
export type ContractStatus = Contract['status'];
export type ContractFrequency = NonNullable<Contract['frequency']>;
// --- Vertrags-Mapping: Client-Gruppierung ---------------------------------
// Hand-gepflegte Interfaces nach dem abgestimmten API-Vertrag (Backend
// entsteht parallel). Sobald `pnpm gen:types` die neuen Schemas aus
// docs/api/openapi.yaml liefert, können diese auf Schemas['…']-Aliase
// umgestellt werden. Sie ersetzen die früheren Wizard-Typen
// UnmappedContract / UnmappedContractList / MatchSuggestion / AutoMatchReport.

/** Kompakte Vertrags-Zeile innerhalb einer Client-Gruppe. */
export interface ClientContractSummary {
  id: UUID;
  number: string;
  amount: number;
  status: string;
  next_send_date: ISODate | null;
}

/** Kunden-Vorschlag für einen IN-Client (Top-3, absteigend nach Score). */
export interface ClientMatchSuggestion {
  customer_id: UUID;
  customer_name: string;
  customer_slug: string;
  /** Match-Confidence in [0..1]. */
  score: number;
  reason: string;
}

/** Ein Invoice-Ninja-Client mit allen seinen ungemappten Verträgen. */
export interface UnmappedClientGroup {
  invoice_ninja_client_id: string;
  client_name: string;
  contracts: ClientContractSummary[];
  contracts_count: number;
  contracts_total_amount: number;
  /** Top-3 Vorschläge PRO CLIENT. */
  suggestions: ClientMatchSuggestion[];
}

/** Response von GET /contracts/unmapped (client-gruppiert). */
export interface UnmappedClientsResponse {
  clients: UnmappedClientGroup[];
  total_contracts: number;
  total_unmapped_contracts: number;
  total_unmapped_clients: number;
  total_ignored_clients: number;
}

/** Response von POST /contracts/map-client. */
export interface MapClientResponse {
  mapped_count: number;
}

/** Eintrag aus GET /contracts/ignored-clients. */
export interface IgnoredClient {
  invoice_ninja_client_id: string;
  client_name: string | null;
  note: string | null;
  created_at: ISODateTime;
}

export interface IgnoredClientList {
  data: IgnoredClient[];
}

/** Vorschlag pro Client aus POST /contracts/auto-match. */
export interface ClientAutoMatchProposal {
  client_id: string;
  client_name: string;
  best_suggestion: ClientMatchSuggestion;
  contracts_count: number;
}

/** Response von POST /contracts/auto-match. */
export interface ClientAutoMatchReport {
  proposals: ClientAutoMatchProposal[];
  /** Anzahl gemappter VERTRÄGE (nur bei commit=true > 0). */
  applied_count: number;
  threshold: number;
}

// --- Sprint 2: Maintenance Plans -----------------------------------------
export type MaintenancePlan = Schemas['MaintenancePlan'];
export type MaintenancePlanList = Schemas['MaintenancePlanList'];
export type MaintenancePlanItem = Schemas['MaintenancePlanItem'];
export type MaintenancePlanCreate = Schemas['MaintenancePlanCreate'];
export type MaintenancePlanUpdate = Schemas['MaintenancePlanUpdate'];
export type MaintenancePlanStatus = NonNullable<MaintenancePlan['status']>;
export type MaintenancePlanItemFrequency = MaintenancePlanItem['frequency'];

// --- Sprint 2: Stats / Window-Parsing ------------------------------------
export type CustomerStats = Schemas['CustomerStats'];
export type MaintenanceWindowSpec = Schemas['MaintenanceWindowSpec'];
export type MaintenanceWindowSpecFrequency = NonNullable<MaintenanceWindowSpec['frequency']>;
export type MaintenanceWindowParseResult = Schemas['MaintenanceWindowParseResult'];
export type ActiveContractStatus = NonNullable<CustomerStats['active_contract_status']>;

// --- Sprint 2: HNF-Integration -------------------------------------------
export type HnfCustomer = Schemas['HnfCustomer'];
export type HnfLink = Schemas['HnfLink'];
export type HnfStatus = Schemas['HnfStatus'];

// --- Sprint 3: Sessions / SSH-Config -------------------------------------
export type Session = Schemas['Session'];
export type SessionStatus = Session['status'];
/**
 * ActiveHost wird vom Backend Sprint-3-Carry-Over (WT-304) optional mit
 * `customer_name` und `system_hostname` angereichert, damit die UI nicht pro
 * Zeile nachfragen muss. Beide Felder fehlen wenn die Resolve-Joins keinen
 * Treffer haben - dann fällt die UI auf `host_alias`-Parsing zurück.
 *
 * Wir lokal-erweitern den generierten Schema-Type, weil openapi-typescript
 * die Schema-Änderung noch nicht im OpenAPI-Document hat. `pnpm gen:types`
 * wird das später automatisch überschreiben - dann kann diese Erweiterung
 * entfallen, sobald das Backend-Schema gemerged ist.
 */
export type ActiveHost = Schemas['ActiveHost'] & {
  customer_id?: string | null;
  customer_name?: string | null;
  system_hostname?: string | null;
};
export type SshConfigResult = Schemas['SshConfigResult'];
export type SshConfigState = Schemas['SshConfigState'];

// --- Fälligkeitsliste (Wartung) ------------------------------------------
/**
 * Handgepflegte Typen für GET /maintenance/due, weil das OpenAPI-Dokument
 * die neuen Schemas noch nicht enthält. Nach dem nächsten `pnpm gen:types`
 * durch `Schemas['MaintenanceDue…']`-Aliase ersetzen (gleiches Vorgehen wie
 * seinerzeit bei `ActiveHost`).
 */
export type MaintenanceFrequency =
  | 'monthly'
  | 'quarterly'
  | 'biannual'
  | 'yearly'
  | 'on_demand'
  | 'custom';

export type MaintenanceDueStatus = 'overdue' | 'due_soon' | 'ok';
export type MaintenanceFrequencySource = 'plan' | 'customer';

export interface MaintenanceDueWindow {
  start?: string | null;
  end?: string | null;
  days?: string[] | null;
}

export interface MaintenanceDueSuggestedRoutine {
  routine_slug: string;
  severity: string;
}

export interface MaintenanceDueRow {
  customer_id: UUID;
  customer_name: string;
  customer_slug: string;
  due_status: MaintenanceDueStatus;
  never_maintained: boolean;
  due_date: ISODate;
  /** Negativ = überfällig. */
  days_until_due: number;
  last_maintained_at: ISODateTime | null;
  last_run_id: UUID | null;
  frequency: MaintenanceFrequency;
  frequency_source: MaintenanceFrequencySource;
  plan_id: UUID | null;
  systems_in_scope: number;
  maintenance_window: MaintenanceDueWindow | null;
  suggested_routines: MaintenanceDueSuggestedRoutine[];
}

export interface MaintenanceDueMeta {
  generated_at: ISODateTime;
  within_days: number;
  overdue: number;
  due_soon: number;
  ok: number;
  /** Teilmenge von overdue. */
  never_maintained: number;
  on_demand_excluded: number;
  no_systems_excluded: number;
}

export interface MaintenanceDueResponse {
  data: MaintenanceDueRow[];
  meta: MaintenanceDueMeta;
}

// --- Zabbix-Pakete-Viewer (System-Detail) ---------------------------------
/**
 * Handgepflegte Typen für GET /systems/{id}/zabbix-packages, weil das
 * OpenAPI-Dokument noch nicht neu generiert wurde. Nach dem nächsten
 * `pnpm gen:types` durch `Schemas['Zabbix…']`-Aliase ersetzen (gleiches
 * Vorgehen wie bei den MaintenanceDue-Typen oben).
 */
export type ZabbixMatchedBy = 'hostname' | 'shortname' | 'ip';
export type ZabbixNoMatchReason = 'no_candidate' | 'ambiguous';

export interface ZabbixPackageItem {
  name: string;
  version: string;
  arch?: string | null;
  manager?: string | null;
}

export interface ZabbixMatchedHost {
  hostid: string;
  /** Technischer Zabbix-Hostname (host.host). */
  host: string;
  /** Sichtbarer Zabbix-Name (host.name). */
  name: string;
  matched_by: ZabbixMatchedBy;
}

export interface ZabbixPackagesResponse {
  /** null = System keinem Zabbix-Host zuordenbar (siehe reason). */
  matched_host: ZabbixMatchedHost | null;
  reason?: ZabbixNoMatchReason | null;
  /** Zabbix-Hostnamen bei reason=ambiguous. */
  candidates: string[];
  os_name?: string | null;
  kernel?: string | null;
  /** lastclock des Paket-Items in Zabbix. */
  packages_updated_at?: ISODateTime | null;
  packages: ZabbixPackageItem[];
  /**
   * APT-Update-Sicht (Zabbix-Items itkfm.apt.updates / itkfm.apt.security /
   * itkfm.reboot.required). null = Item existiert nicht bzw. hat nie
   * geliefert (Bestands-Host ohne Agent-Neukonfig) - dann nichts anzeigen.
   */
  pending_updates?: number | null;
  security_updates?: number | null;
  reboot_required?: boolean | null;
  updates_checked_at?: ISODateTime | null;
  /** Hinweis, falls das Paket-JSON aus Zabbix nicht lesbar war. */
  error?: string | null;
}

// --- Zabbix-Ingest (Settings-Integrationen, S8-03) -------------------------
export type ZabbixIngestStatusValue = Schemas['ZabbixIngestStatusValue'];
export type ZabbixIntegrationStatus = Schemas['ZabbixIntegrationStatus'];
export type ZabbixSystemCandidate = Schemas['ZabbixSystemCandidate'];
export type ZabbixUnmatchedHost = Schemas['ZabbixUnmatchedHost'];
export type ZabbixUnmatchedReason = ZabbixUnmatchedHost['reason'];
export type ZabbixOrphanedOverride = Schemas['ZabbixOrphanedOverride'];
export type ZabbixUnmatchedResponse = Schemas['ZabbixUnmatchedResponse'];
export type ZabbixIngestRunResponse = Schemas['ZabbixIngestRunResponse'];
// Hostliste fürs System-Formular (GET /integrations/zabbix/hosts).
export type ZabbixHostOption = Schemas['ZabbixHostOption'];
export type ZabbixHostListResponse = Schemas['ZabbixHostListResponse'];

// --- Session-Warmup (S8-02) ------------------------------------------------
export type WarmupResult = Schemas['WarmupResultModel'];
export type WarmupTarget = Schemas['WarmupTargetModel'];
export type WarmupTargetsResponse = Schemas['WarmupTargetsResponse'];
export type AgentStatusResponse = Schemas['AgentStatusResponse'];

// --- Mail: Konfiguration / Vorlagen / Postausgang -------------------------
/**
 * Handgepflegte Typen für die Mail-Domain (Backend entsteht parallel,
 * gebaut strikt gegen den abgestimmten API-Vertrag). Nach dem nächsten
 * `pnpm gen:types` mit aktualisiertem OpenAPI-Dokument auf `Schemas['…']`-
 * Aliase umstellen (Vorgehen wie bei den MaintenanceDue-Typen oben).
 */

/** Response von GET /mail/config (Passwort wird NIE ausgeliefert). */
export interface MailConfig {
  configured: boolean;
  smtp_host: string | null;
  smtp_port: number;
  smtp_from: string | null;
  smtp_user: string | null;
  /**
   * Wenn gesetzt: Testmodus - ALLE Mails gehen an diese Adresse.
   * WIRKSAMER Wert: der app_settings-Eintrag (PUT /mail/settings) gewinnt
   * über die ENV MAIL_REDIRECT_ALL_TO.
   */
  redirect_all_to: string | null;
  auto_send_run_events: boolean;
  announce_days_before: number;
}

export interface MailTestRequest {
  recipient: string | null;
}

export interface MailTestResult {
  sent: boolean;
  effective_recipient: string;
  error: string | null;
}

/**
 * GET/PUT /mail/settings - UI-steuerbarer Testmodus (DEV-Redirect, S8-06).
 * Der DB-Eintrag gewinnt IMMER über die ENV MAIL_REDIRECT_ALL_TO.
 */
export type MailRedirectSettings = Schemas['MailRedirectSettings'];
export type MailRedirectSettingsUpdate = Schemas['MailRedirectSettingsUpdate'];

/**
 * Muss `MailTrigger` im Backend spiegeln (api/schemas/mail.py).
 *
 * Stand 17.08.2026 fehlten hier `wartung_gestartet`/`wartung_abgeschlossen`
 * seit ihrer Einführung (Migration 0016). Folge im Vorlagen-Editor: das
 * Auslöser-Feld stand bei diesen Vorlagen leer, und ein Klick ins Select hat
 * sie still auf 'manual' umgehängt - die Kundenmail wäre danach nie wieder
 * ausgelöst worden, ohne jede Fehlermeldung. Diese Liste ist deshalb
 * vollständig zu halten.
 */
export type MailTemplateTrigger =
  | 'wartung_gestartet'
  | 'wartung_abgeschlossen'
  | 'wartung_bericht'
  | 'wartung_manuell'
  | 'run_started'
  | 'run_finished'
  | 'maintenance_due'
  | 'manual';

/**
 * Woher der HTML-Teil einer Vorlage kommt.
 *
 * 'auto'   - aus dem fertig gerenderten Text abgeleitet. Eine Änderung am
 *            Text wirkt damit auf BEIDE Teile der Mail.
 * 'custom' - aus body_html_template. Der Text ist dann nur noch der Fallback
 *            für Textclients und erreicht die meisten Empfänger NICHT.
 */
export type MailHtmlMode = 'auto' | 'custom';

export interface MailTemplate {
  id: UUID;
  code: string;
  name: string;
  trigger: MailTemplateTrigger;
  subject_template: string;
  body_template: string;
  /** Handgepflegtes HTML - nur bei html_mode 'custom' wirksam. */
  body_html_template?: string | null;
  html_mode: MailHtmlMode;
  is_active: boolean;
  /** Seed-Vorlagen sind nicht löschbar (nur deaktivierbar). */
  is_seed: boolean;
  updated_at: ISODateTime;
  available_variables: string[];
}

export interface MailTemplateList {
  data: MailTemplate[];
}

export interface MailTemplateCreate {
  code: string;
  name: string;
  trigger: MailTemplateTrigger;
  subject_template: string;
  body_template: string;
  body_html_template?: string | null;
  html_mode: MailHtmlMode;
  is_active: boolean;
}

export interface MailTemplateUpdate {
  name?: string;
  trigger?: MailTemplateTrigger;
  subject_template?: string;
  body_template?: string;
  /**
   * null/Leerstring verwirft das handgepflegte HTML. Der HTML-Teil der Mail
   * verschwindet dadurch nicht - er kommt dann aus dem Text.
   */
  body_html_template?: string | null;
  html_mode?: MailHtmlMode;
  is_active?: boolean;
}

/**
 * Vorschau-Anfrage. Ohne die Template-Felder rendert der Server die
 * GESPEICHERTE Vorlage; mit ihnen genau das, was gerade im Editor steht.
 *
 * Der Editor schickt sie deshalb immer mit - sonst zeigt die Vorschau den
 * Stand des letzten Speicherns statt der Änderung, die man gerade prüfen
 * will (Befund 17.08.2026).
 */
export interface MailTemplatePreviewRequest {
  customer_id: UUID | null;
  trigger?: MailTemplateTrigger;
  /** Überschrift im Kopfband des abgeleiteten HTML-Teils. */
  name?: string;
  subject_template?: string;
  body_template?: string;
  body_html_template?: string | null;
  html_mode?: MailHtmlMode;
}

export interface MailTemplatePreview {
  subject: string;
  /** Der Text-Teil (text/plain-Zweig der Mail). */
  body: string;
  /** Der HTML-Teil (text/html-Zweig) - immer gesetzt. */
  body_html: string;
  /** Der Modus, der für diese Vorschau tatsächlich gegriffen hat. */
  html_mode: MailHtmlMode;
  /** true wenn ohne Kunde mit Beispieldaten gerendert wurde. */
  used_sample_data: boolean;
}

export type MailNotificationStatus = 'draft' | 'sent' | 'failed' | 'discarded';

export interface MailNotification {
  id: UUID;
  template_code: string | null;
  trigger: string;
  customer_id: UUID | null;
  customer_name: string | null;
  run_id: UUID | null;
  subject: string;
  body: string;
  /** Gerenderter HTML-Teil; Text bleibt Fallback (multipart/alternative). */
  body_html?: string | null;
  /** Vom Kunden hinterlegte Empfänger. */
  recipients: string[];
  /** Tatsächliche Empfänger (macht MAIL_REDIRECT_ALL_TO sichtbar). */
  effective_recipients: string[];
  status: MailNotificationStatus;
  error: string | null;
  created_at: ISODateTime;
  sent_at: ISODateTime | null;
}

export interface MailNotificationListMeta {
  draft_count: number;
  sent_count: number;
  failed_count: number;
  total: number;
}

export interface MailNotificationList {
  data: MailNotification[];
  meta: MailNotificationListMeta;
}

/** GET/PUT /customers/{id}/notification-recipients. */
export interface NotificationRecipients {
  emails: string[];
  opt_out: boolean;
  invoice_ninja_suggestions: string[];
}

export interface NotificationRecipientsUpdate {
  emails: string[];
  opt_out: boolean;
}

// --- Convenience-Helfer -------------------------------------------------
/**
 * Type-Guard: prüfe ob die Server-Antwort ein RFC-7807-Problem ist.
 */
export function isProblemDetails(value: unknown): value is ProblemDetails {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.title === 'string' && typeof v.status === 'number' && typeof v.code === 'string'
  );
}
